import { MaterialIcon } from '../MaterialIcon';

interface ToastProps {
  title: string;
  message?: string;
  variant?: 'success' | 'warning' | 'error';
}

export function Toast({ title, message, variant = 'success' }: ToastProps) {
  const colors = {
    success: { border: 'border-secondary', icon: 'check_circle', bg: 'bg-secondary/20', text: 'text-secondary' },
    warning: { border: 'border-tertiary', icon: 'warning', bg: 'bg-tertiary/20', text: 'text-tertiary' },
    error: { border: 'border-error', icon: 'error', bg: 'bg-error/20', text: 'text-error' },
  }[variant];

  return (
    <div
      className={`flex items-center gap-4 bg-surface-container-high p-4 rounded-xl border-l-4 ${colors.border} shadow-lg min-w-[280px]`}
    >
      <div className={`${colors.bg} p-2 rounded-lg`}>
        <MaterialIcon name={colors.icon} className={colors.text} filled />
      </div>
      <div>
        <div className="font-label-bold text-label-bold text-on-surface">{title}</div>
        {message && <div className="text-label-sm font-label-sm text-on-surface-variant">{message}</div>}
      </div>
    </div>
  );
}

export function EmptyState({ title, message }: { title: string; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center text-center p-stack-lg">
      <MaterialIcon name="inbox" className="text-4xl text-on-surface-variant mb-4" />
      <h3 className="font-headline-md-mobile text-headline-md-mobile text-on-surface mb-2">{title}</h3>
      <p className="text-on-surface-variant max-w-sm">{message}</p>
    </div>
  );
}

export function ErrorState({ title, message, actionLabel }: { title: string; message: string; actionLabel?: string }) {
  return (
    <div className="bg-surface-container/90 backdrop-blur-2xl border border-white/10 rounded-xl p-stack-lg flex flex-col items-center justify-center min-h-[280px] shadow-xl text-center">
      <div className="w-20 h-20 rounded-full bg-error-container/20 flex items-center justify-center mb-6 border border-error/20">
        <MaterialIcon name="leak_remove" className="text-error text-5xl" filled />
      </div>
      <h3 className="font-headline-md-mobile text-headline-md-mobile text-error mb-2">{title}</h3>
      <p className="text-on-surface-variant max-w-[240px] mx-auto mb-8">{message}</p>
      {actionLabel && (
        <button
          type="button"
          className="w-full bg-primary text-on-primary py-3 px-6 rounded-lg font-label-bold text-label-bold flex items-center justify-center gap-2 active:scale-95 transition-all"
        >
          <MaterialIcon name="home" className="text-[20px]" />
          {actionLabel}
        </button>
      )}
    </div>
  );
}

export function LoadingState({ title, message }: { title: string; message?: string }) {
  return (
    <div className="relative overflow-hidden bg-surface-container/90 backdrop-blur-2xl border border-white/10 rounded-xl p-stack-lg flex flex-col items-center justify-center min-h-[280px] shadow-xl">
      <div className="absolute inset-0 scanline-effect opacity-50" />
      <div className="loading-spinner mb-6" />
      <div className="text-center z-10">
        <h3 className="font-headline-md-mobile text-headline-md-mobile mb-2">{title}</h3>
        {message && <p className="text-on-surface-variant font-medium">{message}</p>}
      </div>
    </div>
  );
}
